// Pure helpers for the Spend page. Everything here is derived per render from
// the API payloads — totals, deltas, percentages, bar geometry, anomaly flags
// — and none of it is stored in component state. Keeping the derivation in
// one place also keeps the four sections consistent: the stat row, the chart
// caption and the mover list all describe the same comparison, so they must
// all compute it the same way.

const MINUS = "−"; // U+2212 MINUS SIGN — not a hyphen. Aligns with digits.

export function daysInMonth(period) {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month, 0).getDate();
}

export function previousPeriod(period) {
  const [year, month] = period.split("-").map(Number);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, "0")}`;
}

export function isCurrentMonth(period) {
  const now = new Date();
  return period === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthDate(period, day = 1) {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function monthShort(period) {
  return monthDate(period).toLocaleDateString(undefined, { month: "short" });
}

export function monthLong(period) {
  return monthDate(period).toLocaleDateString(undefined, { month: "long" });
}

export function periodLabel(period) {
  return monthDate(period).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function periodLabelLong(period) {
  return monthDate(period).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// Stat values and axis labels: no decimals. At 34px a trailing ".00" is noise,
// and cents never change the decision the number is there to support.
export function money(n) {
  return `$${Math.round(Number(n) || 0).toLocaleString()}`;
}

export function money2(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function amount2(n) {
  return (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// One decimal below 10%, none above — a "+8.1%" is a meaningfully different
// claim from "+8%", whereas "+248.3%" pretends to a precision the underlying
// month-to-date figures do not have.
export function pctText(pct) {
  const abs = Math.abs(pct);
  return `${abs < 10 ? abs.toFixed(1) : abs.toFixed(0)}%`;
}

export function formatQuantity(n, unit) {
  const value = Number(n) || 0;
  const text = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return unit ? `${text} ${unit}` : text;
}

/**
 * The shared shape behind every delta on the page.
 *
 * `direction` is "up" | "down" | "flat" and drives colour: red for an
 * increase, green for a decrease. Returns null when both sides round to zero,
 * so callers can render nothing at all rather than a "—" placeholder.
 *
 * A previous value of zero has no percentage, so it is labelled "(new)"
 * rather than the "+Infinity%" the arithmetic would otherwise produce.
 */
export function deltaParts(current, previous) {
  const a = Number(previous) || 0;
  const b = Number(current) || 0;
  const diff = b - a;
  if (Math.abs(a) < 0.005 && Math.abs(b) < 0.005) return null;

  const direction = diff > 0.005 ? "up" : diff < -0.005 ? "down" : "flat";
  const sign = diff > 0 ? "+" : diff < 0 ? MINUS : "";
  const amountText = `${sign}${amount2(Math.abs(diff))}`;

  if (Math.abs(a) < 0.005) {
    return { diff, pct: null, direction, amountText, pctLabel: "new", isNew: true };
  }
  const pct = (diff / Math.abs(a)) * 100;
  return { diff, pct, direction, amountText, pctLabel: `${sign}${pctText(pct)}`, isNew: false };
}

/** "+102.40 (+8.1%)" / "−54.00 (−12%)" / "+9.10 (new)" */
export function deltaText(current, previous) {
  const d = deltaParts(current, previous);
  if (!d) return null;
  return { text: `${d.amountText} (${d.pctLabel})`, direction: d.direction };
}

/**
 * Last month's daily series re-indexed so that index i holds the WEEKDAY-
 * MATCHED day rather than the same day number.
 *
 * This is the difference between a real signal and a manufactured one.
 * Comparing Aug 4 against Jul 4 when one is a Tuesday and the other a
 * Saturday can show a 40%+ jump created entirely by the weekly pattern of the
 * workload — which is exactly the threshold the chart uses to flag an anomaly.
 * For each day of the requested month this picks the day of the previous
 * month with the same weekday that is nearest in date (always inside a week),
 * so a Tuesday is only ever compared against a Tuesday.
 *
 * Returns an array of length daysInMonth(period); entries are null where the
 * matched previous day has no data.
 */
export function alignPreviousByWeekday(period, previousDaily) {
  const prev = previousPeriod(period);
  const days = daysInMonth(period);
  const prevDays = daysInMonth(prev);
  const firstCur = monthDate(period).getDay();
  const firstPrev = monthDate(prev).getDay();

  // Day p of the previous month shares day d's weekday when
  // p ≡ d + (firstCur − firstPrev) (mod 7). Of the two candidates that
  // straddle d, take the nearer one that actually exists in the month.
  const shift = (((firstCur - firstPrev) % 7) + 7) % 7;

  const out = [];
  for (let d = 1; d <= days; d += 1) {
    const ahead = d + shift; // 0–6 days after d
    const behind = ahead - 7; // 1–7 days before d
    let match = shift <= 3 ? ahead : behind;
    if (match < 1 || match > prevDays) match = match === ahead ? behind : ahead;
    if (match < 1 || match > prevDays) {
      out.push(null);
      continue;
    }
    const value = previousDaily?.[match - 1];
    out.push({ day: match, value: value === undefined ? null : Number(value) });
  }
  return out;
}

/**
 * The per-day figure used to fill in the rest of the month.
 *
 * Deliberately the mean of the last seven ACTUAL days rather than a seasonal
 * model: the question users are asking is "if the last week continues, where
 * does this land", and a smarter forecast would answer a question they did
 * not ask. The flip side — that one spike inside that window overstates it —
 * is what the forecast note on screen exists to say out loud.
 */
export function trailingMean(daily, window = 7) {
  if (!daily?.length) return 0;
  const slice = daily.slice(Math.max(0, daily.length - window));
  return slice.reduce((sum, v) => sum + Number(v || 0), 0) / slice.length;
}

export function sum(list) {
  return (list || []).reduce((total, v) => total + Number(v || 0), 0);
}

/** Y-axis scale: round, human tick values that always clear the tallest mark. */
export function axisScale(max) {
  const step = max > 2000 ? 1000 : 500;
  const top = Math.max(step, Math.ceil(max / step) * step);
  const labels = [];
  const gridlines = [];
  for (let v = 0; v <= top; v += step) {
    labels.push(v);
    // The plot's own bottom rule already draws the zero line — a gridline
    // there would sit directly on top of it at half opacity.
    if (v > 0) gridlines.push(v);
  }
  return { step, top, labels, gridlines };
}

// Everyone reading this dashboard works in Manila, so every clock reading on
// the page is shown in Manila time and labelled, rather than left to the
// viewer's machine — a reconciliation that is being checked against a PH
// business day should not read differently on a laptop that is travelling.
export const DISPLAY_TIMEZONE = "Asia/Manila";
export const DISPLAY_TIMEZONE_LABEL = "PHT";

/**
 * A BILLING date, kept in UTC.
 *
 * Cost Explorer bills by UTC day, so "through 24 Aug" names a UTC day, not a
 * Manila one. Converting that date to Manila would roll it forward to the 25th
 * for anything after 16:00 local and claim a day of coverage that does not
 * exist yet. The date stays UTC; only the clock beside it moves.
 */
export function formatBillingDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { timeZone: "UTC", day: "numeric", month: "short" });
}

/** A wall-clock reading in Manila time, 24-hour — "16:00", never "04:00 PM". */
export function formatClock(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, {
    timeZone: DISPLAY_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** True when `iso` falls on today's date in Manila — for "cached 11:12 today". */
export function isTodayInManila(iso) {
  if (!iso) return false;
  const asDay = (d) => d.toLocaleDateString("en-CA", { timeZone: DISPLAY_TIMEZONE });
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return asDay(d) === asDay(new Date());
}

/* ---------------------------------------------------------------------------
   Forecasting

   Two different numbers are shown on the page and they answer different
   questions. Do not quietly merge them.

   - The PACE forecast is computed here: month to date plus an average daily
     rate multiplied by the days remaining. It answers "if the recent run
     continues, where does this land", which is what catches a resource left
     running. The averaging window is the user's to choose, because a 7-day
     window reacts fast but a single spike inside it overstates the month,
     while a month-to-date window is steady but barely moves when spend steps
     up on the 17th.
   - The AWS forecast comes from Cost Explorer's own model (see
     backend/src/handlers/get_cost_daily.py). It is the figure the AWS console
     shows, which is what a finance reconciliation has to agree with.
--------------------------------------------------------------------------- */

export const FORECAST_BASES = [
  { value: "7", label: "7-day", days: 7 },
  { value: "14", label: "14-day", days: 14 },
  { value: "mtd", label: "Month", days: null },
];

export const DEFAULT_FORECAST_BASIS = "7";

export function basisLabel(basis) {
  return (FORECAST_BASES.find((b) => b.value === basis) || FORECAST_BASES[0]).label;
}

/** Average daily spend over the chosen window. */
export function basisPace(series, basis) {
  if (!series?.length) return 0;
  const entry = FORECAST_BASES.find((b) => b.value === basis) || FORECAST_BASES[0];
  return entry.days ? trailingMean(series, entry.days) : sum(series) / series.length;
}

/**
 * Month-end projection from the elapsed days plus the chosen daily pace.
 *
 * Returns { monthEnd, pace, daysRemaining, growth }. `growth` is the factor
 * the month-to-date total is expected to grow by, and it is what every
 * per-row projection in the breakdown is built from.
 */
export function paceForecast(series, daysInMonth, basis) {
  const elapsed = series?.length || 0;
  const mtd = sum(series);
  const pace = basisPace(series, basis);
  const daysRemaining = Math.max(0, (daysInMonth || 0) - elapsed);
  const monthEnd = mtd + pace * daysRemaining;
  return { mtd, monthEnd, pace, daysRemaining, growth: mtd > 0 ? monthEnd / mtd : 1 };
}

/**
 * Project one row (an account, a service, a usage type) to month end.
 *
 * Scaled by the whole month's growth factor rather than modelled per row,
 * because there is no per-row daily series to model from — only a monthly
 * total. So this says "if this row keeps its current share of spend", which
 * is honest and is what the info note on screen states. A row that only
 * started yesterday will therefore be understated; the daily chart is where
 * a step change shows up, not here.
 */
export function projectRow(amount, growth) {
  return (Number(amount) || 0) * (growth || 1);
}

/**
 * How a projected month end sits against a budget.
 *
 * Returns null when no budget is set — which is a different state from a
 * budget of zero, and renders differently.
 */
export function budgetState(projected, budget) {
  if (budget === null || budget === undefined) return null;
  const limit = Number(budget) || 0;
  const over = projected - limit;
  return {
    limit,
    projected,
    over,
    isOver: over > 0.005,
    // Clamped for the bar's width; `pct` keeps the true figure for the label.
    pct: limit > 0 ? (projected / limit) * 100 : projected > 0 ? Infinity : 0,
    fillPct: limit > 0 ? Math.min(100, (projected / limit) * 100) : 100,
  };
}

/**
 * What a `compare=previous` response was measured against, as a label.
 *
 * While a month is in progress the API compares against the SAME days of the
 * previous month (`comparison.mode === "same_point"`), so the label must name
 * those days — "1–12 Sep" — rather than claim the whole of September. Falls
 * back to the month name for a closed month, or for an older API that sends
 * no `comparison` at all.
 */
export function comparisonLabel(comparison, prevPeriod) {
  const prev = comparison?.previous_period || prevPeriod;
  if (comparison?.mode === "same_point" && comparison.through_day) {
    const n = comparison.through_day;
    return n === 1 ? `${monthShort(prev)} 1` : `1–${n} ${monthShort(prev)}`;
  }
  return monthLong(prev);
}
